import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Store, Camera, Video, Save, Plus, X, Upload,
  Clock, Globe, Phone, MapPin, Tag, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [business, setBusiness] = useState(null);
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    website: '',
    opening_hours: '',
    logo: null,
    cover_image: null,
    gallery_images: [],
    video_url: '',
    brands: [],
    local_producers: [],
    // Restaurant-specific
    cuisine_type: '',
    has_click_collect: false,
    has_dine_in: false,
    pmr_accessible: false,
    animals_allowed: false,
    adapted_lighting: false,
    ambiance: '',
    price_range: '',
    producer_subcategories: []
  });

  const [newBrand, setNewBrand] = useState('');
  const [newProducer, setNewProducer] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/business/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Erreur de chargement');
      
      const data = await res.json();
      setBusiness(data);
      setFormData({
        business_name: data.business_name || '',
        description: data.description || '',
        address: data.address || '',
        city: data.city || '',
        postal_code: data.postal_code || '',
        phone: data.phone || '',
        website: data.website || '',
        opening_hours: data.opening_hours || '',
        logo: data.logo || null,
        cover_image: data.cover_image || null,
        gallery_images: data.gallery_images || [],
        video_url: data.video_url || '',
        brands: data.brands || [],
        local_producers: data.local_producers || [],
        cuisine_type: data.cuisine_type || '',
        has_click_collect: data.has_click_collect || false,
        has_dine_in: data.has_dine_in || false,
        pmr_accessible: data.pmr_accessible || false,
        animals_allowed: data.animals_allowed || false,
        adapted_lighting: data.adapted_lighting || false,
        ambiance: data.ambiance || '',
        price_range: data.price_range || '',
        producer_subcategories: data.producer_subcategories || []
      });
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image trop grande (max 2 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (type === 'logo') {
        setFormData({ ...formData, logo: base64 });
      } else if (type === 'cover') {
        setFormData({ ...formData, cover_image: base64 });
      } else if (type === 'gallery') {
        setFormData({ 
          ...formData, 
          gallery_images: [...formData.gallery_images, base64].slice(0, 10) 
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const removeGalleryImage = (index) => {
    setFormData({
      ...formData,
      gallery_images: formData.gallery_images.filter((_, i) => i !== index)
    });
  };

  const addBrand = () => {
    if (newBrand.trim() && !formData.brands.includes(newBrand.trim())) {
      setFormData({ ...formData, brands: [...formData.brands, newBrand.trim()] });
      setNewBrand('');
    }
  };

  const removeBrand = (brand) => {
    setFormData({ ...formData, brands: formData.brands.filter(b => b !== brand) });
  };

  const addProducer = () => {
    if (newProducer.name.trim()) {
      setFormData({ 
        ...formData, 
        local_producers: [...formData.local_producers, { ...newProducer }] 
      });
      setNewProducer({ name: '', description: '' });
    }
  };

  const removeProducer = (index) => {
    setFormData({
      ...formData,
      local_producers: formData.local_producers.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/business/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Erreur de sauvegarde');

      toast.success('Profil mis à jour avec succès');
      navigate('/business/dashboard');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/business/dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <h1 className="font-semibold text-charcoal flex items-center gap-2">
              <Store className="w-5 h-5" />
              Profil de ma boutique
            </h1>
          </div>
          <Button onClick={handleSubmit} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Cover Image */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div 
            className="h-48 bg-gradient-to-r from-gray-200 to-gray-300 relative cursor-pointer group"
            onClick={() => coverInputRef.current?.click()}
          >
            {formData.cover_image ? (
              <img src={formData.cover_image} alt="Cover" className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Ajouter une photo de couverture</p>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Upload className="w-8 h-8 text-white" />
            </div>
            <input 
              ref={coverInputRef}
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={(e) => handleImageUpload(e, 'cover')}
            />
          </div>

          {/* Logo */}
          <div className="px-6 pb-6 -mt-12">
            <div 
              className="w-24 h-24 rounded-xl bg-white shadow-lg border-4 border-white cursor-pointer relative group overflow-hidden"
              onClick={() => logoInputRef.current?.click()}
            >
              {formData.logo ? (
                <img src={formData.logo} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <Store className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <input 
                ref={logoInputRef}
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={(e) => handleImageUpload(e, 'logo')}
              />
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-charcoal mb-4">Informations générales</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block">Nom de la boutique</label>
              <Input
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="Ma Super Épicerie"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Décrivez votre boutique, votre histoire, vos valeurs..."
                className="w-full p-3 border rounded-lg resize-none h-32"
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground mt-1">{formData.description.length}/1000</p>
            </div>
          </div>
        </div>

        {/* Contact & Address */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Coordonnées
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block">Adresse</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Rue de la Paix"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Code postal</label>
              <Input
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                placeholder="75001"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Ville</label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Paris"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block flex items-center gap-1">
                <Phone className="w-3 h-3" /> Téléphone
              </label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="01 23 45 67 89"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block flex items-center gap-1">
                <Globe className="w-3 h-3" /> Site web
              </label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://maboutique.fr"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block flex items-center gap-1">
                <Clock className="w-3 h-3" /> Horaires d'ouverture
              </label>
              <textarea
                value={formData.opening_hours}
                onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })}
                placeholder="Lun-Ven: 9h-19h&#10;Sam: 9h-18h&#10;Dim: Fermé"
                className="w-full p-3 border rounded-lg resize-none h-24"
              />
            </div>
          </div>
        </div>

        {/* Restaurant Settings */}
        {business?.business_type === 'restaurant' && (
          <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="restaurant-settings">
            <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
              🍽️ Paramètres du restaurant
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Type de cuisine</label>
                <select
                  className="w-full h-9 rounded-md border bg-transparent px-3 text-sm"
                  value={formData.cuisine_type}
                  onChange={(e) => setFormData({ ...formData, cuisine_type: e.target.value })}
                  data-testid="cuisine-type-select"
                >
                  <option value="">Sélectionner</option>
                  <option value="Française">Française</option>
                  <option value="Italienne">Italienne</option>
                  <option value="Japonaise">Japonaise</option>
                  <option value="Chinoise">Chinoise</option>
                  <option value="Indienne">Indienne</option>
                  <option value="Mexicaine">Mexicaine</option>
                  <option value="Libanaise">Libanaise</option>
                  <option value="Thaïlandaise">Thaïlandaise</option>
                  <option value="Méditerranéenne">Méditerranéenne</option>
                  <option value="Américaine">Américaine</option>
                  <option value="Fusion">Fusion</option>
                  <option value="Végétarienne">Végétarienne</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Gamme de prix</label>
                <select
                  className="w-full h-9 rounded-md border bg-transparent px-3 text-sm"
                  value={formData.price_range}
                  onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="€">€ (Économique)</option>
                  <option value="€€">€€ (Moyen)</option>
                  <option value="€€€">€€€ (Haut de gamme)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Ambiance sonore</label>
                <select
                  className="w-full h-9 rounded-md border bg-transparent px-3 text-sm"
                  value={formData.ambiance}
                  onChange={(e) => setFormData({ ...formData, ambiance: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="calme">🤫 Calme</option>
                  <option value="modere">🎵 Modéré</option>
                  <option value="anime">🎉 Animé</option>
                </select>
              </div>
            </div>

            <h3 className="font-medium text-sm mt-6 mb-3">Services</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: 'has_dine_in', label: '🍽️ Sur place', testId: 'toggle-dine-in' },
                { key: 'has_click_collect', label: '📦 Click & Collect', testId: 'toggle-click-collect' },
                { key: 'pmr_accessible', label: '♿ PMR accessible', testId: 'toggle-pmr' },
                { key: 'animals_allowed', label: '🐾 Animaux', testId: 'toggle-animals' },
                { key: 'adapted_lighting', label: '💡 Luminosité adaptée', testId: 'toggle-lighting' }
              ].map(({ key, label, testId }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFormData({ ...formData, [key]: !formData[key] })}
                  className={`p-3 rounded-xl text-sm font-medium text-left transition-colors ${
                    formData[key] ? 'bg-primary/10 text-primary border-2 border-primary/30' : 'bg-gray-50 text-muted-foreground border-2 border-transparent'
                  }`}
                  data-testid={testId}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Producer Subcategories */}
        {business?.business_type === 'producteur' && (
          <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="producer-settings">
            <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
              🌾 Mes activités de producteur
            </h2>
            <p className="text-sm text-muted-foreground mb-3">Sélectionnez vos types de production</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { id: "domaine_viticole", label: "Domaine viticole", emoji: "🍇" },
                { id: "ferme", label: "Ferme", emoji: "🐄" },
                { id: "maraicher", label: "Maraîcher", emoji: "🥬" },
                { id: "verger", label: "Verger / Fruits", emoji: "🍎" },
                { id: "apiculteur", label: "Apiculteur", emoji: "🍯" },
                { id: "fromagerie_artisanale", label: "Fromagerie artisanale", emoji: "🧀" },
                { id: "elevage", label: "Élevage", emoji: "🐔" },
                { id: "brasserie", label: "Brasserie artisanale", emoji: "🍺" },
                { id: "conserverie", label: "Conserverie", emoji: "🫙" },
                { id: "oleiculteur", label: "Oléiculteur", emoji: "🫒" },
                { id: "trufficulture", label: "Trufficulture", emoji: "🍄" }
              ].map(sub => {
                const isActive = (formData.producer_subcategories || []).includes(sub.id);
                return (
                  <button
                    key={sub.id}
                    type="button"
                    onClick={() => {
                      const current = formData.producer_subcategories || [];
                      setFormData({
                        ...formData,
                        producer_subcategories: isActive
                          ? current.filter(s => s !== sub.id)
                          : [...current, sub.id]
                      });
                    }}
                    className={`p-3 rounded-xl text-sm text-left transition-colors ${
                      isActive ? 'bg-primary/10 text-primary border-2 border-primary/30' : 'bg-gray-50 text-muted-foreground border-2 border-transparent'
                    }`}
                    data-testid={`producer-sub-${sub.id}`}
                  >
                    {sub.emoji} {sub.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Gallery */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Camera className="w-4 h-4" /> Galerie photos
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {formData.gallery_images.map((img, index) => (
              <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
                <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(index)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {formData.gallery_images.length < 10 && (
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-primary flex items-center justify-center transition-colors"
              >
                <Plus className="w-8 h-8 text-gray-400" />
              </button>
            )}
            <input 
              ref={galleryInputRef}
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={(e) => handleImageUpload(e, 'gallery')}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">{formData.gallery_images.length}/10 photos</p>
        </div>

        {/* Video */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Video className="w-4 h-4" /> Vidéo de présentation
          </h2>
          <Input
            value={formData.video_url}
            onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
            placeholder="https://youtube.com/watch?v=..."
          />
          <p className="text-xs text-muted-foreground mt-1">Lien YouTube ou Vimeo</p>
        </div>

        {/* Brands */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Tag className="w-4 h-4" /> Marques proposées
          </h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {formData.brands.map((brand) => (
              <span 
                key={brand} 
                className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full text-sm"
              >
                {brand}
                <button type="button" onClick={() => removeBrand(brand)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              placeholder="Ajouter une marque"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBrand())}
            />
            <Button type="button" variant="outline" onClick={addBrand}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Local Producers */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" /> Producteurs locaux partenaires
          </h2>
          <div className="space-y-3 mb-4">
            {formData.local_producers.map((producer, index) => (
              <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{producer.name}</p>
                  {producer.description && (
                    <p className="text-sm text-muted-foreground">{producer.description}</p>
                  )}
                </div>
                <button type="button" onClick={() => removeProducer(index)} className="text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <Input
              value={newProducer.name}
              onChange={(e) => setNewProducer({ ...newProducer, name: e.target.value })}
              placeholder="Nom du producteur"
            />
            <div className="flex gap-2">
              <Input
                value={newProducer.description}
                onChange={(e) => setNewProducer({ ...newProducer, description: e.target.value })}
                placeholder="Description (optionnel)"
              />
              <Button type="button" variant="outline" onClick={addProducer}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to="/business/dashboard">Annuler</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </form>
    </div>
  );
}